import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Loader2, X, ZoomIn, RotateCw, Maximize2 } from "lucide-react";

/**
 * ImageCropper — modal cropper used by ImageUploader when `cropAspect`
 * is set. Reads the file as a data URL, lets the user pan/zoom/rotate
 * inside a fixed aspect-ratio frame, and returns a freshly encoded
 * JPEG/PNG Blob via `onConfirm(blob, file)`.
 *
 * Single-file at a time — multi-file callers should iterate.
 */
export default function ImageCropper({
  file,
  aspect: initialAspect = 1,
  outputType,
  outputQuality = 0.92,
  onConfirm,
  onCancel,
  onSkip,
  allowSkip = true,
  // When true (default), show the top-of-modal aspect ratio picker so
  // the user can flip between Free / 1:1 / 3:4 / 4:3 / 16:9. Set to
  // false for consumers (banners, logos) that MUST enforce one ratio.
  lockAspect = false,
  testid = "image-cropper",
}) {
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [areaPx, setAreaPx] = useState(null);
  const [busy, setBusy] = useState(false);
  // `undefined` for Free-form aspect; any number = locked ratio. Seeded
  // from the caller's `initialAspect` on mount so consumers that pass
  // 16/9 for banners still see the correct default.
  const [aspect, setAspect] = useState(initialAspect);

  React.useEffect(() => {
    if (!file) { setSrc(null); return; }
    const reader = new FileReader();
    reader.onload = (e) => setSrc(e.target?.result);
    reader.readAsDataURL(file);
  }, [file]);

  const onCropComplete = useCallback((_area, areaPxNew) => {
    setAreaPx(areaPxNew);
  }, []);

  const confirm = async () => {
    if (!src || !areaPx) return;
    setBusy(true);
    try {
      const mime = outputType || (file.type === "image/png" ? "image/png" : "image/jpeg");
      const blob = await renderCroppedBlob(src, areaPx, rotation, mime, outputQuality);
      // Wrap in a File so the server-side handler sees a proper name.
      const ext = mime === "image/png" ? "png" : "jpg";
      const base = (file.name || "image").replace(/\.[^.]+$/, "");
      const cropped = new File([blob], `${base}-cropped.${ext}`, { type: mime });
      await onConfirm(cropped);
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid={testid}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
    >
      <div className="bg-white border border-[hsl(var(--kb-border))] w-full max-w-2xl shadow-xl flex flex-col">
        <div className="flex items-center justify-between border-b border-[hsl(var(--kb-border))] p-4">
          <div>
            <div className="overline">Crop image</div>
            <div className="text-xs text-[hsl(var(--kb-muted))] mt-0.5">
              {aspect
                ? `Aspect ${aspect === 1 ? "1:1" : aspect.toFixed(2) + ":1"}`
                : "Aspect: Free — drag any rectangle"}
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="p-1 hover:bg-[hsl(var(--kb-surface-alt))]"
            data-testid={`${testid}-close`}
            aria-label="Close"
          ><X size={16} /></button>
        </div>

        {!lockAspect && (
          <div className="flex items-center gap-1 px-4 py-2 border-b border-[hsl(var(--kb-border))] overflow-x-auto">
            <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))] mr-2 shrink-0">Ratio</span>
            {[
              { label: "Free", value: undefined },
              { label: "1:1", value: 1 },
              { label: "3:4", value: 3 / 4 },
              { label: "4:3", value: 4 / 3 },
              { label: "16:9", value: 16 / 9 },
              { label: "9:16", value: 9 / 16 },
            ].map((opt) => {
              const active = (opt.value === undefined && aspect === undefined)
                || (opt.value !== undefined && aspect !== undefined
                    && Math.abs(aspect - opt.value) < 0.01);
              return (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => setAspect(opt.value)}
                  className={`px-2.5 py-1 text-[10px] uppercase tracking-wider border shrink-0 ${
                    active
                      ? "border-[hsl(var(--kb-primary))] bg-[hsl(var(--kb-primary))] text-white"
                      : "border-[hsl(var(--kb-border))] hover:bg-[hsl(var(--kb-surface-alt))]"
                  }`}
                  data-testid={`${testid}-aspect-${opt.label.toLowerCase().replace(':','-')}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}

        <div className="relative w-full bg-black" style={{ height: 420 }}>
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              restrictPosition
              objectFit="contain"
              showGrid={true}
            />
          )}
        </div>

        <div className="p-4 space-y-3 border-t border-[hsl(var(--kb-border))]">
          <div className="flex items-center gap-3">
            <ZoomIn size={14} className="text-[hsl(var(--kb-muted))] shrink-0" />
            <input
              type="range"
              min={1}
              max={4}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-[hsl(var(--kb-primary))]"
              data-testid={`${testid}-zoom`}
            />
            <span className="font-mono text-[10px] w-10 text-right">{zoom.toFixed(2)}x</span>
          </div>
          <div className="flex items-center gap-3">
            <RotateCw size={14} className="text-[hsl(var(--kb-muted))] shrink-0" />
            <input
              type="range"
              min={0}
              max={360}
              step={1}
              value={rotation}
              onChange={(e) => setRotation(Number(e.target.value))}
              className="flex-1 accent-[hsl(var(--kb-primary))]"
              data-testid={`${testid}-rotate`}
            />
            <span className="font-mono text-[10px] w-10 text-right">{rotation}°</span>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t border-[hsl(var(--kb-border))]">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="kb-btn-outline !py-1.5 !px-3 !text-[11px]"
            data-testid={`${testid}-cancel`}
          >Cancel</button>
          <div className="flex items-center gap-2">
            {allowSkip && (
              <button
                type="button"
                onClick={onSkip}
                disabled={busy}
                className="kb-btn-outline !py-1.5 !px-3 !text-[11px] inline-flex items-center gap-1"
                data-testid={`${testid}-skip`}
                title="Upload the original image without cropping"
              ><Maximize2 size={11} /> Use original</button>
            )}
            <button
              type="button"
              onClick={confirm}
              disabled={busy || !areaPx}
              className="kb-btn-primary !py-1.5 !px-3 !text-[11px] inline-flex items-center gap-1"
              data-testid={`${testid}-confirm`}
            >
              {busy && <Loader2 size={11} className="animate-spin" />}
              Crop & upload
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----- helpers --------------------------------------------------------------

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = url;
  });
}

/**
 * Render the cropped + rotated region to a canvas and export as a Blob.
 * Handles arbitrary rotation by drawing onto an enlarged offscreen
 * canvas, then cropping the rotated bounds.
 */
async function renderCroppedBlob(srcUrl, areaPx, rotation, mime, quality) {
  const img = await loadImage(srcUrl);
  const radians = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(radians));
  const cos = Math.abs(Math.cos(radians));
  const boundW = img.width * cos + img.height * sin;
  const boundH = img.width * sin + img.height * cos;

  // 1) Draw rotated full image onto an offscreen canvas.
  const stage = document.createElement("canvas");
  stage.width = boundW;
  stage.height = boundH;
  const ctx = stage.getContext("2d");
  ctx.translate(boundW / 2, boundH / 2);
  ctx.rotate(radians);
  ctx.drawImage(img, -img.width / 2, -img.height / 2);

  // 2) Extract the crop area (pixel-accurate, react-easy-crop gives us
  // coordinates relative to the rotated image's natural pixel space).
  const out = document.createElement("canvas");
  out.width = areaPx.width;
  out.height = areaPx.height;
  const outCtx = out.getContext("2d");
  outCtx.drawImage(
    stage,
    areaPx.x, areaPx.y, areaPx.width, areaPx.height,
    0, 0, areaPx.width, areaPx.height,
  );

  return new Promise((resolve) =>
    out.toBlob((b) => resolve(b), mime, quality),
  );
}
